import { useState, useRef, useEffect } from 'react';
import { Modal, Button, ButtonActionToggle } from '@plick/ui';
import toast from 'react-hot-toast';
import { plickTheme } from '@theme/index';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import type { FounderMessageFormProps } from '@interfaces/Feedback';
import { FEEDBACK_MIN_CHARACTERS, FEEDBACK_TEMPLATES } from '@utils/constants';

const FounderMessageForm = ({ commerce, onClose }: FounderMessageFormProps) => {
  const [message, setMessage] = useState('');
  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { trackEvent } = useTracking();

  const characterCount = message.trim().length;
  const isValidLength = characterCount >= FEEDBACK_MIN_CHARACTERS;
  const remainingChars = FEEDBACK_MIN_CHARACTERS - characterCount;

  useEffect(() => {
    // Track form modal opened
    trackEvent(TRACK_EVENT_NAMES.FeedbackFormModalOpened);
  }, [trackEvent]);

  const handleClose = () => {
    trackEvent(TRACK_EVENT_NAMES.FeedbackFormCanceled);
    setIsOpen(false);
    setTimeout(onClose, 300); // Wait for animation
  };

  const handleSend = async () => {
    if (!isValidLength) return;

    setIsLoading(true);
    setError(null);

    try {
      // Importamos el cliente de Supabase
      const { supabase } = await import('@utils/supabase/client');

      // Validar que el teléfono existe y convertirlo a string
      if (!commerce.telefono) {
        throw new Error('Número de teléfono no disponible');
      }
      const phone = String(commerce.telefono);

      // 1. Buscar o crear conversación
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .select('id')
        .eq('phone_number', phone)
        .eq('status', 'active')
        .single();

      if (convError && convError.code !== 'PGRST116') {
        throw convError;
      }

      let conversationId: string;

      if (!conversation) {
        // Crear nueva conversación
        const { data: newConv, error: createError } = await supabase
          .from('conversations')
          .insert([
            {
              commerce_id: String(commerce.id),
              phone_number: phone,
              status: 'active',
            },
          ])
          .select('id')
          .single();

        if (createError) throw createError;
        conversationId = newConv!.id;
      } else {
        conversationId = conversation.id;
      }

      // 2. Guardar mensaje con source='panel'
      const { data: newMessage, error: msgError } = await supabase
        .from('messages')
        .insert([
          {
            conversation_id: conversationId,
            sender_type: 'user',
            content: message,
            source: 'panel',
            metadata: {
              commerceId: String(commerce.id),
              commerceName: commerce.nombre,
              contactName: commerce.nombre_contacto,
              timestamp: new Date().toISOString(),
            },
          },
        ])
        .select('id')
        .single();

      if (msgError) throw msgError;

      // 3. Actualizar last_message_at en conversación
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', conversationId);

      if (updateError) throw updateError;

      // 4. Track evento (ahora sabemos que SÍ se guardó)
      trackEvent(TRACK_EVENT_NAMES.FeedbackFormSubmitted, {
        messageLength: characterCount,
        commerceId: commerce.id,
        commerceName: commerce.nombre,
        messageId: newMessage!.id,
      });

      // 5. Notificar a Slack via Edge Function
      const { error: notifyError } = await supabase.functions.invoke('panel-feedback-notify', {
        body: { messageId: newMessage!.id },
      });

      // 6. Toast de éxito y cerrar
      if (notifyError) {
        toast.success('Tu mensaje fue guardado, pero la notificación al equipo falló. Lo revisaremos.');
      } else {
        toast.success('¡Gracias! Tu mensaje fue enviado al equipo de Plick.');
      }
      handleClose();
    } catch {
      setError('No se pudo enviar el feedback. Por favor intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateClick = (templateText: string, templateLabel: string) => {
    trackEvent(TRACK_EVENT_NAMES.FeedbackTemplateClicked, {
      template: templateLabel,
    });

    setMessage(templateText);
    // Focus the textarea and move cursor to the end
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        // Move cursor to the end of the text
        const length = templateText.length;
        textareaRef.current.setSelectionRange(length, length);
      }
    }, 0);
  };

  return (
    <Modal padding="0" maxHeight="90vh" maxWidth="42rem" isOpen={isOpen} onClose={handleClose}>
      <div className="p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-bold text-color-primary mb-3 md:mb-4">Ayúdanos a Mejorar</h2>

        <div className="mb-3 md:mb-4">
          <p className="text-xs md:text-sm text-gray-600 mb-2">
            Los siguientes datos se enviarán junto con tu mensaje:
          </p>
          <ul className="list-disc list-inside text-xs md:text-sm text-gray-500 mb-3 md:mb-4">
            <li>Comercio: {commerce.nombre}</li>
            <li>Contacto: {commerce.nombre_contacto}</li>
            <li>Teléfono: {commerce.telefono || 'No registrado'}</li>
          </ul>
        </div>

        <div className="mb-3 md:mb-4">
          <label className="block text-xs md:text-sm font-medium mb-2">Plantillas rápidas:</label>
          <div className="flex flex-wrap gap-1.5 md:gap-2">
            {FEEDBACK_TEMPLATES.map((template) => (
              <div key={template.id} className="text-xs md:text-sm">
                <ButtonActionToggle
                  color={plickTheme.colors.textBase}
                  defaultBgColor={plickTheme.colors.complementary}
                  selectedBgColor={plickTheme.colors.secondaryLight}
                  selected={template.value === message}
                  text={template.label}
                  onClick={() => handleTemplateClick(template.value, template.label)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4 md:mb-6">
          <label className="block text-xs md:text-sm font-medium mb-1">Describe tu idea, mejora o feedback</label>
          <textarea
            ref={textareaRef}
            className={`w-full p-2 border-2 rounded-md  ${
              characterCount > 0 && !isValidLength
                ? 'border-color-red_notification'
                : isValidLength
                  ? 'border-color-green'
                  : 'border-color-gray_ultra_dark'
            }`}
            rows={4}
            placeholder="Tu mensaje... (mín. 100 caracteres)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          {characterCount > 0 && !isValidLength && (
            <p className="text-[10px] md:text-xs text-color-red_notification mt-1">
              Por favor, escribe mínimo {remainingChars} caracteres para enviar tu mensaje.
            </p>
          )}
        </div>

        {error && (
          <div className="mb-3 p-2 bg-color-red_notification/10 border border-color-red_notification rounded">
            <p className="text-xs text-color-red_notification">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button.Tertiary text="CANCELAR" onClick={handleClose} />
          <Button.Primary
            text={isLoading ? 'GUARDANDO...' : 'ENVIAR'}
            onClick={handleSend}
            disabled={!isValidLength || isLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default FounderMessageForm;
